import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from './AuthContext';
import RichTextEditor from './RichTextEditor';
import { CloseIcon, FileIcon, PaperclipIcon, SendIcon, ZapIcon, SearchIcon } from './Icons';
import { formatFileSize } from './emailAvatar';
import {
  EMAIL_ADDRESS_PATTERN,
  MAX_ATTACHMENT_BYTES,
  fillTemplate,
  newRequestId,
  relativeTime,
  signatureHtml,
  uploadEmailAttachment,
} from './emailUtils';

// Quick replies are fetched once per session and shared by every composer.
// The settings screen fires "email-quick-replies-changed" after an edit.
let quickRepliesCache = null;
let quickRepliesRequest = null;
const loadQuickReplies = (authFetch) => {
  if (quickRepliesCache) return Promise.resolve(quickRepliesCache);
  if (!quickRepliesRequest) {
    quickRepliesRequest = authFetch('/api/email/quick-replies')
      .then((response) => response.json().then((data) => ({ ok: response.ok, data })))
      .then(({ ok, data }) => {
        if (!ok) throw new Error(data.error || 'Could not load quick replies');
        quickRepliesCache = Array.isArray(data) ? data : [];
        return quickRepliesCache;
      })
      .finally(() => {
        quickRepliesRequest = null;
      });
  }
  return quickRepliesRequest;
};
if (typeof window !== 'undefined') {
  window.addEventListener('email-quick-replies-changed', () => {
    quickRepliesCache = null;
  });
}

const squashSpace = (value) => String(value || '').replace(/\s+/g, '');

// Unsent drafts, per conversation, for this browser tab only: switching to
// another conversation and back doesn't lose what was typed.
const drafts = new Map();

function QuickReplyPicker({ onPick, onClose }) {
  const { authFetch } = useAuth();
  const [replies, setReplies] = useState(quickRepliesCache || []);
  const [loading, setLoading] = useState(!quickRepliesCache);
  const [error, setError] = useState('');
  const [term, setTerm] = useState('');
  const [active, setActive] = useState(0);
  const rootRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    loadQuickReplies(authFetch)
      .then((list) => !cancelled && setReplies(list))
      .catch((err) => !cancelled && setError(err.message))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [authFetch]);

  useEffect(() => {
    const handleClick = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) onClose();
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onClose]);

  const filtered = useMemo(() => {
    const needle = term.trim().toLowerCase().replace(/^\//, '');
    if (!needle) return replies;
    return replies.filter(
      (reply) =>
        reply.name.toLowerCase().includes(needle) ||
        (reply.shortcut || '').toLowerCase().includes(needle) ||
        reply.body.toLowerCase().includes(needle)
    );
  }, [replies, term]);

  useEffect(() => setActive(0), [term]);

  const handleKeyDown = (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((index) => Math.min(index + 1, filtered.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((index) => Math.max(index - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (filtered[active]) onPick(filtered[active]);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
    }
  };

  return (
    <div className="ew-quick-picker" ref={rootRef} role="dialog" aria-label="Quick replies">
      <div className="ew-quick-search">
        <SearchIcon width={14} height={14} />
        <input
          type="text"
          value={term}
          onChange={(event) => setTerm(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Search quick replies"
          aria-label="Search quick replies"
          autoFocus
        />
      </div>
      <div className="ew-quick-list" role="listbox">
        {loading ? (
          <div className="ew-quick-empty">Loading…</div>
        ) : error ? (
          <div className="ew-quick-empty">{error}</div>
        ) : filtered.length === 0 ? (
          <div className="ew-quick-empty">{replies.length === 0 ? 'No quick replies yet. An admin can add them in Email settings.' : 'No match.'}</div>
        ) : (
          filtered.map((reply, index) => (
            <button
              type="button"
              key={reply.id}
              role="option"
              aria-selected={index === active}
              className={`ew-quick-item ${index === active ? 'ew-quick-item-active' : ''}`}
              onMouseEnter={() => setActive(index)}
              onClick={() => onPick(reply)}
            >
              <span className="ew-quick-item-top">
                <strong>{reply.name}</strong>
                {reply.shortcut && <span className="ew-quick-shortcut">/{reply.shortcut}</span>}
              </span>
              <span className="ew-quick-item-body">{reply.body.replace(/\s+/g, ' ').slice(0, 110)}</span>
            </button>
          ))
        )}
      </div>
      <div className="ew-quick-hint">↑↓ to choose · Enter to insert · nothing is sent until you press Send</div>
    </div>
  );
}

// One composer for every way of sending email: a reply in a conversation,
// a new email, a forward, and a reply from the Mailbox view. Everything goes
// through POST /api/customers/:key/send-email (server/email/routes.js).
//
// mode: 'reply' | 'new' | 'forward'
// conversationKey: where a reply goes (phone key or address). For 'new' and
//   'forward' the typed To address is used instead.
// forwardOf: the stored message being forwarded (its attachments can be
//   included); replyToMessageId / inReplyTo (+ inReplyToRef, its mailbox
//   { folder, uid }): what a reply answers.
// lastSeenMessageId: newest message the agent has seen, so the server can
//   warn when a colleague replied meanwhile.
function EmailComposer({
  mode = 'reply',
  conversationKey,
  initialTo = '',
  toLocked = false,
  toSuggestions = [],
  initialSubject = '',
  replyToMessageId = null,
  inReplyTo = null,
  inReplyToRef = null,
  forwardOf = null,
  lastSeenMessageId = null,
  templateValues = {},
  signature = '',
  draftKey = null,
  onSent,
  onCancel,
  autoFocus = true,
  sendLabel = 'Send',
}) {
  const { authFetch, token } = useAuth();
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);
  const requestIdRef = useRef(null);
  const mountedRef = useRef(true);
  const [to, setTo] = useState(initialTo);
  const [subject, setSubject] = useState(initialSubject);
  const [editingSubject, setEditingSubject] = useState(mode !== 'reply');
  const [bodyText, setBodyText] = useState('');
  const [files, setFiles] = useState([]); // { id, file, status, progress, ref, error }
  const [includeOriginalFiles, setIncludeOriginalFiles] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [conflict, setConflict] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  // Browsers report the editor's text with different line breaks, so the
  // "is this only the signature?" check ignores whitespace.
  const signatureKey = useMemo(() => (String(signature || '').trim() ? squashSpace(`--${signature}`) : ''), [signature]);
  const isBlank = (text) => {
    const key = squashSpace(text);
    return !key || key === signatureKey;
  };

  // The latest subject/to, for the draft.
  const latest = useRef({ subject, to });
  latest.current = { subject, to };

  // Keeps the draft current on every edit (not on close: by the time a
  // closing composer's cleanup runs, React has already detached the editor).
  const persistDraft = () => {
    const editor = editorRef.current;
    if (!draftKey || !editor) return;
    if (!isBlank(editor.getText())) {
      drafts.set(draftKey, { html: editor.getHTML(), ...latest.current });
    } else {
      drafts.delete(draftKey);
    }
  };

  // Fresh composer: a saved draft if there is one, otherwise empty lines
  // above the agent's signature, with the cursor at the top.
  useEffect(() => {
    mountedRef.current = true;
    const editor = editorRef.current;
    const saved = draftKey ? drafts.get(draftKey) : null;
    if (saved) {
      editor?.setHTML(saved.html);
      if (saved.subject != null) setSubject(saved.subject);
      if (saved.to != null && !toLocked) setTo(saved.to);
    } else if (signature) {
      editor?.setHTML(signatureHtml(signature));
    }
    if (autoFocus) {
      requestAnimationFrame(() => {
        if (mode === 'new' && !toLocked && !initialTo) return;
        editor?.focusStart();
      });
    }
    return () => {
      mountedRef.current = false;
    };
    // Only on mount/unmount: later prop changes must not wipe what's typed.
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (draftKey && drafts.has(draftKey)) drafts.set(draftKey, { ...drafts.get(draftKey), subject, to });
  }, [draftKey, subject, to]);

  const hasMessage = () => !isBlank(editorRef.current?.getText());

  const uploading = files.some((entry) => entry.status === 'uploading');
  const failedUploads = files.some((entry) => entry.status === 'error');
  const doneFiles = files.filter((entry) => entry.status === 'done');
  const originalAttachments = forwardOf?.attachments || [];

  const updateFile = (id, patch) =>
    setFiles((previous) => previous.map((entry) => (entry.id === id ? { ...entry, ...patch } : entry)));

  const addFiles = (list) => {
    const incoming = Array.from(list || []);
    if (incoming.length === 0) return;
    const fresh = [];
    for (const file of incoming) {
      const duplicate = files.some(
        (entry) => entry.file.name === file.name && entry.file.size === file.size && entry.file.lastModified === file.lastModified
      );
      if (duplicate) continue;
      const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      if (file.size > MAX_ATTACHMENT_BYTES) {
        fresh.push({ id, file, status: 'error', progress: 0, error: 'Larger than 15 MB' });
        continue;
      }
      fresh.push({ id, file, status: 'uploading', progress: 0 });
    }
    setFiles((previous) => [...previous, ...fresh]);
    // Uploaded straight away, so Send doesn't wait on them.
    fresh
      .filter((entry) => entry.status === 'uploading')
      .forEach((entry) => {
        uploadEmailAttachment({
          file: entry.file,
          conversationKey: conversationKey || 'upload',
          token,
          onProgress: (progress) => mountedRef.current && updateFile(entry.id, { progress }),
        })
          .then((ref) => mountedRef.current && updateFile(entry.id, { status: 'done', progress: 100, ref }))
          .catch((err) => mountedRef.current && updateFile(entry.id, { status: 'error', error: err.message }));
      });
  };

  const removeFile = (id) => setFiles((previous) => previous.filter((entry) => entry.id !== id));

  const resetAfterSend = () => {
    if (draftKey) drafts.delete(draftKey);
    editorRef.current?.setHTML(signature ? signatureHtml(signature) : '');
    setFiles([]);
    setConflict(null);
    setError('');
  };

  const targetKey = () => {
    if (mode === 'reply') return conversationKey;
    return to.trim().toLowerCase();
  };

  const send = async ({ force = false } = {}) => {
    if (sending) return;
    const key = targetKey();
    if (mode !== 'reply' && !EMAIL_ADDRESS_PATTERN.test(key || '')) {
      setError('Enter a valid email address in To');
      return;
    }
    if (!key) {
      setError('No email address for this conversation');
      return;
    }
    const includeForwardFiles = Boolean(forwardOf) && includeOriginalFiles && originalAttachments.length > 0;
    if (!hasMessage() && doneFiles.length === 0 && !forwardOf) {
      setError('Write a message first');
      editorRef.current?.focus();
      return;
    }
    if (uploading) return;
    if (failedUploads) {
      setError('Remove the attachments that failed to upload first');
      return;
    }

    setSending(true);
    setError('');
    if (!requestIdRef.current) requestIdRef.current = newRequestId();
    try {
      const response = await authFetch(`/api/customers/${encodeURIComponent(key)}/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subject: subject.trim(),
          body: hasMessage() ? editorRef.current.getText().trim() : '',
          html: hasMessage() ? editorRef.current.getHTML() : '',
          attachments: doneFiles.map((entry) => entry.ref),
          clientRequestId: requestIdRef.current,
          lastSeenMessageId,
          force,
          replyToMessageId: replyToMessageId ?? undefined,
          inReplyTo: inReplyTo ?? undefined,
          inReplyToRef: inReplyToRef ?? undefined,
          forwardOf: forwardOf ? { messageId: forwardOf.id, includeAttachments: includeForwardFiles } : undefined,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.status === 409 && data.conflict) {
        setConflict(data.conflict);
        setSending(false);
        return;
      }
      if (!response.ok) throw new Error(data.error || 'Send failed');
      requestIdRef.current = null;
      resetAfterSend();
      setSending(false);
      onSent?.(data);
    } catch (err) {
      setError(err.message || 'Send failed');
      setSending(false);
    }
  };

  const handleEditorKeyDown = (event) => {
    if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
      event.preventDefault();
      send();
    } else if (event.key === 'Escape' && onCancel && !hasMessage()) {
      event.preventDefault();
      onCancel();
    }
  };

  const pickQuickReply = (reply) => {
    setPickerOpen(false);
    const editor = editorRef.current;
    if (!editor) return;
    const filled = fillTemplate(reply.body, templateValues);
    if (!hasMessage()) editor.focusStart();
    else editor.focus();
    editor.insertText(filled);
    setBodyText(editor.getText());
    if (mode !== 'reply' && !subject.trim() && reply.subject) setSubject(fillTemplate(reply.subject, templateValues));
  };

  const handleDrop = (event) => {
    if (!event.dataTransfer?.files?.length) return;
    event.preventDefault();
    addFiles(event.dataTransfer.files);
  };

  const sendDisabled = sending || uploading;

  return (
    <div
      className={`ew-composer ew-composer-${mode}`}
      onDragOver={(event) => event.dataTransfer?.types?.includes('Files') && event.preventDefault()}
      onDrop={handleDrop}
    >
      {mode !== 'reply' && (
        <label className="ew-composer-row">
          <span className="ew-composer-label">To</span>
          <input
            type="email"
            value={to}
            onChange={(event) => setTo(event.target.value)}
            placeholder="customer@example.com"
            disabled={toLocked || sending}
            list={toSuggestions.length > 0 ? 'ew-to-suggestions' : undefined}
            autoFocus={autoFocus && !toLocked && !initialTo}
            aria-label="To"
          />
          {toSuggestions.length > 0 && (
            <datalist id="ew-to-suggestions">
              {toSuggestions.map((item) => (
                <option key={item.email} value={item.email}>
                  {item.name || item.email}
                </option>
              ))}
            </datalist>
          )}
        </label>
      )}

      {editingSubject ? (
        <label className="ew-composer-row">
          <span className="ew-composer-label">Subject</span>
          <input
            type="text"
            value={subject}
            onChange={(event) => setSubject(event.target.value)}
            placeholder="Subject"
            disabled={sending}
            aria-label="Subject"
          />
        </label>
      ) : (
        <button type="button" className="ew-composer-subject-line" onClick={() => setEditingSubject(true)} title="Edit subject">
          <span className="ew-composer-label">Subject</span>
          <span className="ew-composer-subject-text">{subject || '(no subject)'}</span>
          <span className="ew-composer-subject-edit">Edit</span>
        </button>
      )}

      {conflict && (
        <div className="ew-composer-conflict" role="alert">
          <span>
            <strong>{conflict.sentBy || 'Someone'}</strong> replied {relativeTime(conflict.createdAt)}
            {conflict.subject ? ` ("${conflict.subject}")` : ''}. Check the conversation before sending a second answer.
          </span>
          <span className="ew-composer-conflict-actions">
            <button type="button" className="btn btn-secondary btn-small" onClick={() => setConflict(null)}>
              Review first
            </button>
            <button type="button" className="btn btn-primary btn-small" onClick={() => send({ force: true })} disabled={sending}>
              Send anyway
            </button>
          </span>
        </div>
      )}

      <RichTextEditor
        ref={editorRef}
        placeholder={mode === 'forward' ? 'Add a note (optional)…' : mode === 'new' ? 'Write your message…' : 'Write your reply…'}
        ariaLabel="Message"
        disabled={sending}
        onChange={(text) => {
          setBodyText(text);
          persistDraft();
        }}
        onKeyDown={handleEditorKeyDown}
      />

      {(files.length > 0 || originalAttachments.length > 0) && (
        <div className="ew-composer-files">
          {originalAttachments.length > 0 && (
            <label className="ew-composer-include">
              <input
                type="checkbox"
                checked={includeOriginalFiles}
                onChange={(event) => setIncludeOriginalFiles(event.target.checked)}
                disabled={sending}
              />
              Include the original {originalAttachments.length === 1 ? 'attachment' : `${originalAttachments.length} attachments`}
              <span className="ew-muted">({originalAttachments.map((item) => item.filename || 'file').join(', ')})</span>
            </label>
          )}
          {files.map((entry) => (
            <span
              key={entry.id}
              className={`ew-file-chip ${entry.status === 'error' ? 'ew-file-chip-error' : ''}`}
              title={entry.error || entry.file.name}
            >
              <FileIcon width={13} height={13} />
              <span className="ew-file-name">{entry.file.name}</span>
              <span className="ew-file-meta">
                {entry.status === 'uploading'
                  ? `${entry.progress}%`
                  : entry.status === 'error'
                  ? entry.error
                  : formatFileSize(entry.file.size)}
              </span>
              {entry.status === 'uploading' && (
                <span className="ew-file-progress" style={{ width: `${entry.progress}%` }} aria-hidden="true" />
              )}
              <button
                type="button"
                className="ew-file-remove"
                onClick={() => removeFile(entry.id)}
                aria-label={`Remove ${entry.file.name}`}
                disabled={sending}
              >
                <CloseIcon width={11} height={11} />
              </button>
            </span>
          ))}
        </div>
      )}

      {error && (
        <div className="ew-composer-error" role="alert">
          {error}
        </div>
      )}

      <div className="ew-composer-footer">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          hidden
          onChange={(event) => {
            addFiles(event.target.files);
            event.target.value = '';
          }}
        />
        <button
          type="button"
          className="ew-tool-btn"
          onClick={() => fileInputRef.current?.click()}
          disabled={sending}
          title="Attach files (or drop them here)"
          aria-label="Attach files"
        >
          <PaperclipIcon width={15} height={15} />
        </button>
        <span className="ew-quick-anchor">
          <button
            type="button"
            className={`ew-tool-btn ew-tool-btn-text ${pickerOpen ? 'ew-tool-btn-active' : ''}`}
            onClick={() => setPickerOpen((open) => !open)}
            disabled={sending}
            aria-haspopup="dialog"
            aria-expanded={pickerOpen}
          >
            <ZapIcon width={14} height={14} />
            Quick reply
          </button>
          {pickerOpen && <QuickReplyPicker onPick={pickQuickReply} onClose={() => setPickerOpen(false)} />}
        </span>
        <span className="ew-composer-hint">{uploading ? 'Uploading…' : 'Ctrl+Enter to send'}</span>
        {onCancel && (
          <button type="button" className="btn btn-secondary btn-small" onClick={onCancel} disabled={sending}>
            {isBlank(bodyText) ? 'Cancel' : 'Close'}
          </button>
        )}
        <button type="button" className="btn btn-primary btn-small ew-send-btn" onClick={() => send()} disabled={sendDisabled}>
          {sending ? 'Sending…' : sendLabel}
          <SendIcon width={13} height={13} />
        </button>
      </div>
    </div>
  );
}

export default EmailComposer;
